import axios from 'src/config/axios.config';

import { API_ENDPOINTS } from 'src/config/api.config';
import { storage } from 'src/utils/storage';
import { isAxiosError } from 'axios';

// User interface
export interface UserDto {
  id: string;
  name: string;
  username: string;
  password?: string;
  accountNonExpired: boolean;
  isEnabled: boolean;
  accountNonLocked: boolean;
  credentialsNonExpired: boolean;
  authorities?: string[];
  role?: string;
}

export interface CreateUserDto {
  name: string;
  username: string;
  password: string;
  isEnabled?: boolean;
}

// New interface for the complete user creation request
export interface CreateUserRequest {
  name: string;
  username: string;
  password: string | null;
  authorities: string[];
  companyId: string;
  branchId?: string;
  position?: string;
  sendActivationEmail: boolean;
}

export interface UpdateUserDto extends Partial<CreateUserDto> {
  accountNonExpired?: boolean;
  accountNonLocked?: boolean;
  credentialsNonExpired?: boolean;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Users API fonksiyonları
export const usersApi = {
  // Tüm kullanıcıları getir
  getAll: async (page = 0, size = 10, sortBy = 'name', sortDirection = 'asc'): Promise<PageResponse<UserDto>> => {
    const response = await axios.get<UserDto[]>(`${API_ENDPOINTS.USERS}`, {
      params: { page, size, sortBy, sortDirection }
    });
    
    // Backend sadece array döndürüyor, PageResponse format'ına çeviriyoruz
    const users = response.data;
    return {
      content: users,
      totalElements: users.length,
      totalPages: Math.ceil(users.length / size),
      size: users.length,
      number: page
    };
  },

  // Basit kullanıcı listesi (dropdown/selection için)
  getSimpleList: async (): Promise<{ id: string; name: string; username: string; role?: string }[]> => {
    const response = await axios.get<UserDto[]>(`${API_ENDPOINTS.USERS}`);
    return response.data.map(user => ({
      id: user.id,
      name: user.name,
      username: user.username,
      role: user.role || 'USER'
    }));
  },

  // Kullanıcı detaylarını getir
  getById: async (id: string): Promise<UserDto> => {
    const response = await axios.get<UserDto>(`${API_ENDPOINTS.USERS}/id/${id}`);
    return response.data;
  },

  // Username ile kullanıcı getir
  getByUsername: async (username: string): Promise<UserDto> => {
    const response = await axios.get<UserDto>(`${API_ENDPOINTS.USERS}/username/${username}`);
    return response.data;
  },

  // Yeni kullanıcı oluştur
  create: async (data: CreateUserDto): Promise<UserDto> => {
    const response = await axios.post<UserDto>(`${API_ENDPOINTS.USERS}`, data);
    return response.data;
  },

  // Yeni kullanıcı oluştur (tam format)
  createUser: async (data: CreateUserRequest): Promise<UserDto> => {
    const response = await axios.post<UserDto>(`${API_ENDPOINTS.USERS}`, data);
    return response.data;
  },

  // Kullanıcı güncelle
  update: async (id: string, data: UpdateUserDto): Promise<UserDto> => {
    const response = await axios.put<UserDto>(`${API_ENDPOINTS.USERS}/${id}`, data);
    return response.data;
  },

  // Kullanıcı sil
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_ENDPOINTS.USERS}/${id}`);
  },

  // Backend'den yeni endpoint: Kullanıcının yönettiği çalışanları getir
  getEmployeesForUser: async (userId: string, page: number = 0, size: number = 10, sortBy: string = 'firstName', sortDirection: string = 'asc'): Promise<{content: UserDto[], totalElements: number, totalPages: number, size: number, number: number}> => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.EMPLOYEES}/for-user/${userId}`, {
        params: {
          page,
          size,
          sortBy,
          sortDirection
        }
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          storage.remove('token');
          window.location.href = '/sign-in';
          throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        }
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için yetkiniz bulunmuyor.');
        }
        throw new Error(error.response?.data?.message || 'Çalışanlar alınırken hata oluştu');
      }
      throw new Error('Çalışanlar alınırken hata oluştu');
    }
  },

getEmployeesForBranch: async (branchId: string, page: number = 0, size: number = 10, sortBy: string = 'firstName', sortDirection: string = 'asc'): Promise<{
  data: any;content: UserDto[], totalElements: number, totalPages: number, size: number, number: number
}> => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.EMPLOYEES}/by-branch/${branchId}`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection
      }
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/sign-in';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      throw new Error(error.response?.data?.message || 'Çalışanlar alınırken hata oluştu');
    }
    throw new Error('Çalışanlar alınırken hata oluştu');
  }
}

};

// Export interfaces ve fonksiyonları
export interface ExportEmployeeParams {
  format: 'PDF' | 'EXCEL';
  companyId?: string;
  branchId?: string;
  fileName?: string;
  exportType?: 'current' | 'company' | 'branch' | 'all';
}

// Manager/Admin için employee export
export const exportEmployees = async (params: ExportEmployeeParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.branchId) queryParams.append('branchId', params.branchId);
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axios.get(
      `/api/export/employees?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için yetkiniz bulunmuyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Çalışan export işlemi sırasında bir hata oluştu');
  }
};

// Super Admin için tüm çalışanları export
export const exportAllEmployees = async (params: ExportEmployeeParams): Promise<Blob> => {
  try {
    const token = storage.get('token');
    if (!token) {
      throw new Error('Token bulunamadı');
    }

    // Query parametrelerini oluştur
    const queryParams = new URLSearchParams();
    
    // Backend enum değerleri: PDF, EXCEL
    queryParams.append('format', params.format);
    
    if (params.fileName) queryParams.append('fileName', params.fileName);

    const response = await axios.get(
      `/api/export/employees/all?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': '*/*'
        },
        responseType: 'blob', // Dosya indirme için blob response
      }
    );

    return response.data;
    
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        storage.remove('token');
        window.location.href = '/api/auth/login';
        throw new Error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      }
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için Super Admin yetkisi gerekiyor.');
      }
      if (error.response?.status === 400) {
        throw new Error('Geçersiz parametreler. Lütfen kontrol edin.');
      }
    }
    throw new Error('Super Admin employee export işlemi sırasında bir hata oluştu');
  }
};

// File download helper for employees
export const downloadEmployeeFile = (blob: Blob, fileName: string, format: 'PDF' | 'EXCEL') => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Backend'den gelen dosya adını kullan, yoksa default oluştur
    let finalFileName = fileName;
    if (!fileName || !fileName.includes('.')) {
      const extension = format === 'PDF' ? '.pdf' : '.xlsx';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      finalFileName = `calisanlar-${timestamp}${extension}`;
    }
    
    link.download = finalFileName;
    
    // Dosyayı indir
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Memory cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
    
    const fileSize = blob.size;
    const fileSizeKB = (fileSize / 1024).toFixed(2);
    
  } catch (error) {
    throw new Error('Dosya indirilemedi');
  }
}; 